import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ArrowDown, Copy, Check, Radio, Megaphone, Settings2, Wallet, Share2 } from 'lucide-react';
import { useRest, restFetch, RCard, RHead, RestLoading, SourceBadge, money, RestReconnect } from './restui';
import PixelStatusCard from './PixelStatusCard';
import { TEAM_AVATARS } from '../../components/teamAvatars';

/* Restaurant Intelligence — Today's Business funnel + channel attribution.
   Data: Kitxens Pixel (restaurant's own website) + imported sales reports. */

const RANGES = [
  { d: 1, en: 'Today', es: 'Hoy' }, { d: 7, en: '7 days', es: '7 días' },
  { d: 14, en: '14 days', es: '14 días' }, { d: 30, en: '30 days', es: '30 días' },
  { d: 90, en: '90 days', es: '90 días' }, { d: 180, en: '6 months', es: '6 meses' },
  { d: 365, en: 'Year', es: 'Año' },
];

const CHANNEL_LABEL = {
  direct: ['Direct', 'Directo'], google: ['Google Search', 'Google Search'],
  instagram: ['Instagram', 'Instagram'], facebook: ['Facebook', 'Facebook'],
  'chatgpt.com': ['ChatGPT', 'ChatGPT'], 'gemini.google.com': ['Gemini', 'Gemini'],
  'yelp.com': ['Yelp', 'Yelp'],
};

function FunnelStep({ label, value, accent, last, testid, onClick, selected, clickable, delta, tip }) {
  const up = typeof delta === 'number' && delta >= 0;
  return (
    <div className="flex flex-col items-center h-full" data-testid={testid}>
      <div className="relative group w-full flex-1 flex flex-col">
        <button type="button" onClick={onClick} disabled={!clickable}
          className={`w-full flex-1 rounded-xl border p-3 text-center flex flex-col justify-start transition-[transform,box-shadow,border-color] duration-150 ${
            accent ? 'border-[var(--rest-accent)] bg-[var(--rest-accent-soft)]'
              : selected ? 'border-[var(--rest-accent)] bg-white shadow-md -translate-y-0.5'
                : 'border-gray-200 bg-white'} ${
            clickable ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md hover:border-[var(--rest-accent)]/60' : 'cursor-default'}`}>
          <span className="min-h-[26px] flex items-center justify-center text-[10.5px] uppercase tracking-wider text-gray-400 font-bold leading-[13px]">{label}</span>
          <span className={`block text-[22px] leading-[26px] font-bold tabular-nums ${accent || selected ? 'text-[var(--rest-accent)]' : 'text-gray-900'}`}>{value}</span>
          <span className={`block min-h-[16px] text-[10.5px] font-bold tabular-nums ${typeof delta === 'number' ? (up ? 'text-emerald-600' : 'text-red-500') : 'invisible'}`} data-testid={`${testid}-delta`}>
            {typeof delta === 'number' ? `${up ? '▲' : '▼'} ${Math.abs(delta).toFixed(0)}%` : '·'}
          </span>
        </button>
        {tip && (
          <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-30 w-56 rounded-xl bg-[#141518] border border-white/10 p-3 text-left opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-2xl hidden sm:block"
            data-testid={`${testid}-tip`}>
            <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-[#F77F11]">{tip.title}</p>
            {tip.rows.map(([tk, tv]) => (
              <p key={tk} className="mt-1 flex justify-between gap-3 text-[11px] text-white/75">
                <span>{tk}</span><b className="tabular-nums text-white">{tv}</b>
              </p>
            ))}
          </div>
        )}
      </div>
      <span className="min-h-[22px] flex items-center">
        {!last && <ArrowDown className="w-3.5 h-3.5 text-gray-300 rotate-[-90deg] hidden lg:block" />}
        {!last && <ArrowDown className="w-3.5 h-3.5 text-gray-300 lg:hidden" />}
      </span>
    </div>
  );
}

function PixelInstallCard({ isEs, me }) {
  const { data: p } = useRest('/pixel');
  const [copied, setCopied] = useState(false);
  const [delegated, setDelegated] = useState(false);
  const [showHow, setShowHow] = useState(false);
  if (!p) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(p.snippet);
      setCopied(true);
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = p.snippet;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
      } catch { /* clipboard unavailable */ }
    }
    setTimeout(() => setCopied(false), 2000);
  };
  const delegate = async () => {
    const r = await restFetch('/pixel/delegate', { method: 'POST' });
    if (r.ok) setDelegated(true);
  };
  return (
    <RCard className="p-6 mb-6" testid="rest-pixel-card">
      <div className="flex flex-wrap items-center gap-3">
        <Radio className={`w-5 h-5 ${p.installed ? 'text-emerald-500' : 'text-amber-500'}`} />
        <div className="flex-1 min-w-[220px]">
          <h2 className="text-[15px] font-semibold text-gray-900">
            Kitxens Pixel {p.installed
              ? <span className="ml-2 text-[11px] font-bold text-emerald-600 uppercase" data-testid="pixel-status-live">● {isEs ? 'En vivo' : 'Live'} · {p.events_24h} {isEs ? 'eventos 24h' : 'events 24h'}</span>
              : <span className="ml-2 text-[11px] font-bold text-amber-600 uppercase" data-testid="pixel-status-pending">{isEs ? 'Pendiente de instalar' : 'Not installed yet'}</span>}
          </h2>
          <p className="text-[12.5px] text-gray-500">
            {isEs
              ? 'Un script ligero en tu web convierte cada visita en inteligencia de negocio: llamadas, WhatsApps, reservas, pedidos y de qué canal vino cada cliente.'
              : 'One lightweight script on your website turns every visit into business intelligence: calls, WhatsApps, reservations, orders and which channel brought each guest.'}
          </p>
        </div>
      </div>
      {!p.installed && (
        <div className="mt-4">
          <div className="flex items-center gap-2 rounded-xl bg-gray-900 px-4 py-3">
            <code className="flex-1 text-[11.5px] text-emerald-300 overflow-x-auto whitespace-nowrap" data-testid="pixel-snippet">{p.snippet}</code>
            <button onClick={copy} data-testid="pixel-copy-btn" className="shrink-0 text-white/70 hover:text-white transition-colors">
              {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button onClick={() => setShowHow(!showHow)} data-testid="pixel-how-btn"
              className="text-[12.5px] font-semibold text-[var(--rest-accent)] hover:underline">
              {isEs ? '¿Cómo lo instalo? (2 minutos)' : 'How do I install it? (2 minutes)'}
            </button>
            {me?.role !== 'demo' && !delegated && !p.delegated && (
              <button onClick={delegate} data-testid="pixel-delegate-btn"
                className="rounded-full bg-[var(--rest-accent)] hover:opacity-90 text-white font-bold text-[12px] px-4 py-1.5 transition-opacity">
                {isEs ? 'Prefiero que Kitxens lo instale por mí →' : 'Let Kitxens install it for me →'}
              </button>
            )}
            {(delegated || p.delegated) && (
              <span className="text-[12.5px] font-semibold text-emerald-600" data-testid="pixel-delegated-msg">
                ✓ {isEs ? 'Solicitud enviada — nuestro equipo te contactará hoy.' : 'Request sent — our team will reach out today.'}
              </span>
            )}
          </div>
          {showHow && (
            <ol className="mt-3 space-y-1.5 text-[12.5px] text-gray-600 list-decimal pl-5" data-testid="pixel-instructions">
              <li>{isEs ? 'Copia el código de arriba (botón de copiar).' : 'Copy the code above (copy button).'}</li>
              <li>{isEs ? <>Pégalo en tu web justo antes de <code className="bg-gray-100 px-1 rounded">&lt;/head&gt;</code> — en WordPress: Apariencia → Editor de temas o un plugin como "Insert Headers"; en Wix/Squarespace: Ajustes → Código personalizado.</> : <>Paste it right before <code className="bg-gray-100 px-1 rounded">&lt;/head&gt;</code> on your website — WordPress: Appearance → Theme editor or an "Insert Headers" plugin; Wix/Squarespace: Settings → Custom code.</>}</li>
              <li>{isEs ? 'Guarda y visita tu web — en menos de 1 minuto verás "En vivo" aquí.' : 'Save and visit your site — within 1 minute this card shows "Live".'}</li>
              <li>{isEs ? '¿Prefieres no tocarlo? Usa el botón y lo instalamos nosotros.' : 'Rather not touch code? Use the button and we install it for you.'}</li>
            </ol>
          )}
        </div>
      )}
    </RCard>
  );
}

function TicketEditor({ isEs, tickets, canEdit, onSaved }) {
  const [open, setOpen] = useState(false);
  const [order, setOrder] = useState(tickets.avg_order_value);
  const [resv, setResv] = useState(tickets.avg_reservation_value);
  const save = async () => {
    const r = await restFetch('/intelligence/settings', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ avg_order_value: Number(order), avg_reservation_value: Number(resv) }),
    });
    if (r.ok) { setOpen(false); onSaved(); }
  };
  if (!canEdit) return null;
  return (
    <span className="relative">
      <button onClick={() => setOpen(!open)} data-testid="tickets-edit-btn"
        className="inline-flex items-center gap-1 text-[11.5px] text-gray-400 hover:text-[var(--rest-accent)] transition-colors">
        <Settings2 className="w-3.5 h-3.5" /> {isEs ? `Ticket: $${tickets.avg_order_value}/orden · $${tickets.avg_reservation_value}/reserva` : `Ticket: $${tickets.avg_order_value}/order · $${tickets.avg_reservation_value}/reservation`}
      </button>
      {open && (
        <span className="absolute right-0 top-6 z-20 w-64 rounded-xl border border-gray-200 bg-white p-4 shadow-xl block" data-testid="tickets-popover">
          <label className="block text-[11px] font-bold text-gray-500 uppercase">{isEs ? 'Ticket promedio por orden ($)' : 'Avg ticket per order ($)'}</label>
          <input type="number" value={order} onChange={(e) => setOrder(e.target.value)} data-testid="tickets-order-input"
            className="mt-1 w-full h-9 rounded-lg border border-gray-300 px-2 text-[13px]" />
          <label className="block mt-3 text-[11px] font-bold text-gray-500 uppercase">{isEs ? 'Ticket promedio por reserva ($)' : 'Avg ticket per reservation ($)'}</label>
          <input type="number" value={resv} onChange={(e) => setResv(e.target.value)} data-testid="tickets-resv-input"
            className="mt-1 w-full h-9 rounded-lg border border-gray-300 px-2 text-[13px]" />
          <button onClick={save} data-testid="tickets-save-btn"
            className="mt-3 w-full rounded-full bg-[var(--rest-accent)] text-white font-bold text-[12px] py-2">{isEs ? 'Guardar' : 'Save'}</button>
        </span>
      )}
    </span>
  );
}

function AiTeamRotator({ isEs, channels, connections, onReconnect }) {
  const slides = useMemo(() => {
    const s = [];
    const label = (k) => (CHANNEL_LABEL[k] || [k, k])[isEs ? 1 : 0];
    (channels || []).forEach((c) => {
      if ((connections || {})[c.channel] === false && (c.est_revenue || 0) > 0) {
        s.push({
          who: 'eva',
          msg: isEs
            ? <><b>{label(c.channel)} está desconectado</b> — en este periodo dejaste de captar <b>~{money(c.est_revenue)}</b> (estimado) al no dejar intervenir a Kitxens.</>
            : <><b>{label(c.channel)} is disconnected</b> — this period you missed <b>~{money(c.est_revenue)}</b> (estimated) by not letting Kitxens step in.</>,
          cta: { label: isEs ? 'Reconectar →' : 'Reconnect →', run: () => onReconnect(c.channel) },
        });
      }
    });
    const idle = (channels || []).find((c) => (connections || {})[c.channel] !== false && c.visits > 3 && !c.orders && !c.reservations);
    if (idle) {
      s.push({
        who: 'penny',
        msg: isEs
          ? <><b>{label(idle.channel)}</b> trae <b>{idle.visits} visitas</b> pero aún 0 pedidos — una promoción con CTA directo puede convertirlas en ventas (estimado).</>
          : <><b>{label(idle.channel)}</b> brings <b>{idle.visits} visits</b> but 0 orders yet — a promo with a direct CTA can turn them into sales (estimated).</>,
        cta: { label: isEs ? 'Crear promoción' : 'Create promotion', href: `https://wa.me/14085901102?text=${encodeURIComponent(isEs ? `Quiero una promoción para el canal ${label(idle.channel)}` : `I want a promotion for the ${label(idle.channel)} channel`)}` },
      });
    }
    const best = [...(channels || [])].filter((c) => (connections || {})[c.channel] !== false).sort((a, b) => (b.est_revenue || 0) - (a.est_revenue || 0))[0];
    if (best && best.est_revenue > 0) {
      s.push({
        who: 'rachel',
        msg: isEs
          ? <><b>{label(best.channel)}</b> es tu canal más rentable ahora: <b>{money(best.est_revenue)}</b> estimados. Duplicar su contenido suele amplificar el resultado.</>
          : <><b>{label(best.channel)}</b> is your top earning channel right now: <b>{money(best.est_revenue)}</b> estimated. Doubling its content usually amplifies the result.</>,
        cta: { label: isEs ? 'Hablar con mi AI Team' : 'Talk to my AI Team', href: 'https://wa.me/14085901102' },
      });
    }
    s.push({
      who: 'sonny',
      msg: isEs
        ? <>Los restaurantes que responden reseñas y publican 3+ veces por semana venden más. Tu AI Team puede hacerlo por ti — cada dato de este panel es una oportunidad.</>
        : <>Restaurants that answer reviews and post 3+ times a week sell more. Your AI Team can do it for you — every number on this panel is an opportunity.</>,
      cta: { label: isEs ? 'Quiero vender más →' : 'I want to sell more →', href: 'https://wa.me/14085901102' },
    });
    return s;
  }, [channels, connections, isEs, onReconnect]);
  const [i, setI] = useState(0);
  useEffect(() => {
    if (slides.length < 2) return undefined;
    const t = setInterval(() => setI((v) => v + 1), 7000);
    return () => clearInterval(t);
  }, [slides.length]);
  if (!slides.length) return null;
  const s = slides[i % slides.length];
  const av = TEAM_AVATARS.find((a) => a.key === s.who);
  return (
    <div className="rounded-xl bg-[#1A1A1A] p-4 flex flex-col" data-testid="intel-aiteam-card">
      <div className="flex items-center gap-2.5">
        <img key={s.who} src={av?.img} alt={av?.name} className="w-8 h-8 rounded-full object-cover border border-white/25" />
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: 'var(--rest-accent)' }}>{av?.name} · AI Team</p>
      </div>
      <p key={i % slides.length} className="mt-2 flex-1 text-[12px] leading-relaxed text-white/90" data-testid="intel-aiteam-msg">{s.msg}</p>
      <div className="mt-2.5 flex items-center justify-between gap-2">
        {s.cta?.run ? (
          <button onClick={s.cta.run} className="rounded-full bg-white text-gray-900 text-[11px] font-bold px-3 py-1.5 hover:opacity-90 transition-opacity" data-testid="intel-aiteam-cta">{s.cta.label}</button>
        ) : (
          <a href={s.cta.href} target="_blank" rel="noopener noreferrer" className="rounded-full bg-white text-gray-900 text-[11px] font-bold px-3 py-1.5 hover:opacity-90 transition-opacity" data-testid="intel-aiteam-cta">{s.cta.label}</a>
        )}
        <span className="flex items-center gap-1">
          {slides.map((_, di) => (
            <button key={di} onClick={() => setI(di)} className={`h-1 rounded-full transition-all ${di === i % slides.length ? 'w-4 bg-[var(--rest-accent)]' : 'w-1 bg-white/30'}`} aria-label={`slide ${di + 1}`} />
          ))}
        </span>
      </div>
    </div>
  );
}

export default function RestIntelligence() {
  const { isEs, me } = useOutletContext();
  const [days, setDays] = useState(7);
  const [sel, setSel] = useState(null);
  const { data: d, err, loading, reload } = useRest(`/intelligence?days=${days}`);
  const toggleChannel = async (channel, connected) => {
    await restFetch('/channels/toggle', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ channel, connected }) });
    reload();
  };
  if (err) return <RestReconnect />;
  if (!d && loading) return <RestLoading />;
  if (!d) return null;

  const f = d.funnel;
  const hasPixelData = f.visitors > 0 || f.page_views > 0;
  const cmpTxt = days === 1 ? (isEs ? 'ayer' : 'yesterday')
    : days === 7 ? (isEs ? 'semana anterior' : 'previous week')
      : days === 30 ? (isEs ? 'mes anterior' : 'previous month')
        : days === 90 ? (isEs ? 'trimestre anterior' : 'previous quarter')
          : days === 365 ? (isEs ? 'año anterior' : 'previous year')
            : (isEs ? `${days} días anteriores` : `previous ${days} days`);
  const sinceStr = (d.window?.since || '').slice(0, 10);
  const todayStr = new Date().toISOString().slice(0, 10);
  const stepTip = (s) => {
    const raw = s.k === 'revenue' ? f.estimated_revenue : Number(s.v) || 0;
    const dl = d.compare ? d.compare[s.k === 'revenue' ? 'est_revenue' : s.k] : null;
    const fmtN = (n) => (s.k === 'revenue' ? money(n) : Math.round(n).toLocaleString());
    const rows = [[isEs ? 'Este periodo' : 'This period', fmtN(raw)]];
    if (typeof dl === 'number') {
      rows.push([isEs ? `Vs ${cmpTxt}` : `Vs ${cmpTxt}`, fmtN(raw / (1 + dl / 100))]);
      rows.push([isEs ? 'Cambio' : 'Change', `${dl >= 0 ? '▲ +' : '▼ '}${dl}%`]);
    }
    if (days > 1) rows.push([isEs ? 'Promedio/día' : 'Avg/day', fmtN(raw / days)]);
    return { title: `${sinceStr} → ${todayStr}`, rows };
  };

  // Shareable summary card — the owner sends this month's numbers over WhatsApp in one tap.
  const shareWhatsApp = () => {
    const name = me?.restaurant?.name || 'Kitxens';
    const label = days === 1 ? (isEs ? 'Hoy' : 'Today')
      : days === 365 ? (isEs ? 'Últimos 12 meses' : 'Last 12 months')
        : (isEs ? `Últimos ${days} días` : `Last ${days} days`);
    const top = [...(d.attribution.channels || [])].sort((a, b) => (b.est_revenue || 0) - (a.est_revenue || 0))[0];
    const topName = top ? (CHANNEL_LABEL[top.channel] || [top.channel, top.channel])[isEs ? 1 : 0] : null;
    const dl = d.compare?.est_revenue;
    const chg = typeof dl === 'number' ? ` (${dl >= 0 ? '▲ +' : '▼ '}${dl}% vs ${cmpTxt})` : '';
    const lines = isEs ? [
      `*${name}* — Restaurant Intelligence`,
      `${label} (${sinceStr} → ${todayStr})`,
      '',
      `Visitantes: ${f.visitors.toLocaleString()}`,
      `Leads: ${f.leads.toLocaleString()}`,
      `Llamadas: ${f.calls.toLocaleString()} · WhatsApp: ${f.whatsapp.toLocaleString()}`,
      `Reservas: ${f.reservations.toLocaleString()} · Pedidos: ${f.orders.toLocaleString()}`,
      `Revenue estimado: ${money(f.estimated_revenue)}${chg}`,
      ...(topName ? [`Canal top: ${topName}`] : []),
      '',
      'Generado con KITXENS — kitxens.com',
    ] : [
      `*${name}* — Restaurant Intelligence`,
      `${label} (${sinceStr} → ${todayStr})`,
      '',
      `Visitors: ${f.visitors.toLocaleString()}`,
      `Leads: ${f.leads.toLocaleString()}`,
      `Calls: ${f.calls.toLocaleString()} · WhatsApp: ${f.whatsapp.toLocaleString()}`,
      `Reservations: ${f.reservations.toLocaleString()} · Orders: ${f.orders.toLocaleString()}`,
      `Estimated revenue: ${money(f.estimated_revenue)}${chg}`,
      ...(topName ? [`Top channel: ${topName}`] : []),
      '',
      'Generated with KITXENS — kitxens.com',
    ];
    window.open(`https://wa.me/?text=${encodeURIComponent(lines.join('\n'))}`, '_blank', 'noopener');
  };
  const steps = [
    { k: 'visitors', en: 'Visitors', es: 'Visitantes', v: f.visitors, metric: 'visits' },
    { k: 'leads', en: 'Leads', es: 'Leads', v: f.leads, metric: null },
    { k: 'qualified', en: 'Qualified', es: 'Calificados', v: f.qualified_leads, metric: null },
    { k: 'calls', en: 'Calls', es: 'Llamadas', v: f.calls, metric: 'calls' },
    { k: 'whatsapp', en: 'WhatsApp', es: 'WhatsApp', v: f.whatsapp, metric: 'whatsapp' },
    { k: 'reservations', en: 'Reservations', es: 'Reservas', v: f.reservations, metric: 'reservations' },
    { k: 'orders', en: 'Orders', es: 'Pedidos', v: f.orders, metric: 'orders' },
    { k: 'revenue', en: 'Est. Revenue', es: 'Revenue Est.', v: money(f.estimated_revenue), accent: true, metric: 'est_revenue' },
  ];

  return (
    <div data-testid="rest-intelligence">
      <RHead
        title="Restaurant Intelligence"
        sub={isEs ? 'Tu negocio en números que cualquiera entiende en 5 segundos — de tu propia web, en tiempo real.' : 'Your business in numbers anyone understands in 5 seconds — from your own website, live.'}
        right={(
          <div className="flex items-center gap-1 rounded-full border border-gray-200 bg-white p-1 max-w-full overflow-x-auto" data-testid="intel-range-selector">
            {RANGES.map((rg) => (
              <button key={rg.d} onClick={() => setDays(rg.d)} data-testid={`intel-range-${rg.d}`}
                className={`rounded-full px-3 py-1.5 text-[12px] font-semibold transition-colors whitespace-nowrap shrink-0 ${days === rg.d ? 'bg-[var(--rest-accent)] text-white' : 'text-gray-500 hover:text-gray-900'}`}>
                {isEs ? rg.es : rg.en}
              </button>
            ))}
          </div>
        )}
      />

      <PixelStatusCard isEs={isEs} canInstall={['owner', 'kitxens_admin', 'demo'].includes(me?.role)} />

      {/* Level 2 — period business */}
      <RCard className="p-6" testid="intel-funnel">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-[15px] font-semibold text-gray-900 flex items-center gap-2">
            <Wallet className="w-4 h-4 text-[var(--rest-accent)]" />
            {days === 1 ? (isEs ? 'El negocio de hoy' : "Today's Business")
              : days === 365 ? (isEs ? 'Últimos 12 meses' : 'Last 12 months')
                : (isEs ? `Últimos ${days} días` : `Last ${days} days`)}
            {d.compare && (
              <span className="text-[11px] font-semibold text-gray-400 normal-case" data-testid="intel-compare-label">
                {days === 1 ? (isEs ? 'vs ayer' : 'vs yesterday')
                  : days === 7 ? (isEs ? 'vs semana anterior' : 'vs previous week')
                    : days === 30 ? (isEs ? 'vs mes anterior' : 'vs previous month')
                      : days === 90 ? (isEs ? 'vs trimestre anterior' : 'vs previous quarter')
                        : days === 365 ? (isEs ? 'vs año anterior (est.)' : 'vs previous year (est.)')
                          : (isEs ? `vs ${days} días anteriores` : `vs previous ${days} days`)}
              </span>
            )}
          </h2>
          <div className="flex items-center gap-3">
            {hasPixelData && (
              <button onClick={shareWhatsApp} data-testid="intel-share-whatsapp"
                className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-bold text-[11.5px] px-3 py-1.5 transition-colors">
                <Share2 className="w-3.5 h-3.5" /> {isEs ? 'Compartir por WhatsApp' : 'Share on WhatsApp'}
              </button>
            )}
            <TicketEditor isEs={isEs} tickets={d.tickets} canEdit={['owner', 'kitxens_admin'].includes(me?.role)} onSaved={reload} />
            <SourceBadge source={hasPixelData ? 'connected_api' : 'not_connected'} isEs={isEs} />
          </div>
        </div>
        {hasPixelData ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 items-stretch">
              {steps.map((s, i) => (
                <FunnelStep key={s.k} label={isEs ? s.es : s.en} value={s.v} accent={s.accent} last={i === steps.length - 1}
                  testid={`funnel-${s.k}`} clickable={!!s.metric} delta={d.compare && s.metric ? d.compare[s.k === 'revenue' ? 'est_revenue' : s.k] : undefined}
                  tip={stepTip(s)}
                  selected={sel === s.k} onClick={() => s.metric && setSel(sel === s.k ? null : s.k)} />
              ))}
            </div>
            {sel && (() => {
              const st = steps.find((s) => s.k === sel);
              const chs = [...(d.attribution.channels || [])].sort((a, b) => (b[st.metric] || 0) - (a[st.metric] || 0));
              const mx = Math.max(...chs.map((c) => c[st.metric] || 0), 1);
              return (
                <div className="mt-4 border-t border-gray-100 pt-4" data-testid="intel-breakdown">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-3">
                    {isEs ? st.es : st.en} · {isEs ? 'desglose por canal' : 'breakdown by channel'}
                  </p>
                  <div className="space-y-2">
                    {chs.map((c) => (
                      <div key={c.channel} className="flex items-center gap-3" data-testid={`breakdown-${c.channel}`}>
                        <span className="w-28 shrink-0 text-[12px] font-semibold text-gray-700 truncate">{(CHANNEL_LABEL[c.channel] || [c.channel, c.channel])[isEs ? 1 : 0]}</span>
                        <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full rounded-full kx-grow-x bg-[var(--rest-accent)]" style={{ width: `${Math.max(2, 100 * (c[st.metric] || 0) / mx)}%` }} />
                        </div>
                        <b className="w-16 text-right text-[12px] tabular-nums text-gray-800">{st.k === 'revenue' ? money(c.est_revenue) : (c[st.metric] || 0)}</b>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {d.coherence?.web_share_pct != null && (
              <div className="mt-4 rounded-xl bg-[var(--rest-accent-soft)] border border-[var(--rest-accent)]/20 px-4 py-3" data-testid="intel-coherence">
                <p className="text-[12.5px] text-gray-800 leading-snug">
                  {isEs
                    ? <><b>{money(f.estimated_revenue)}</b> atribuibles a tu web con Kitxens — el <b>{d.coherence.web_share_pct}%</b> de tus ventas reales del periodo ({money(d.coherence.sales_total)}).</>
                    : <><b>{money(f.estimated_revenue)}</b> attributable to your website with Kitxens — <b>{d.coherence.web_share_pct}%</b> of your real sales this period ({money(d.coherence.sales_total)}).</>}
                </p>
              </div>
            )}
            <p className="mt-3 text-[11px] text-gray-400">
              {isEs
                ? `Toca una métrica para ver su desglose por canal · Fuente: Kitxens Pixel en tu web · Revenue estimado = pedidos × $${d.tickets.avg_order_value} + reservas × $${d.tickets.avg_reservation_value} (configurable) · También: ${f.directions} direcciones, ${f.menu_downloads} menús vistos, ${f.review_clicks} clics a reseñas`
                : `Tap a metric to see its channel breakdown · Source: Kitxens Pixel on your website · Estimated revenue = orders × $${d.tickets.avg_order_value} + reservations × $${d.tickets.avg_reservation_value} (configurable) · Also: ${f.directions} directions, ${f.menu_downloads} menu views, ${f.review_clicks} review clicks`}
            </p>
          </>
        ) : (
          <p className="text-[13.5px] text-gray-500 py-6 text-center" data-testid="intel-funnel-empty">
            {isEs
              ? 'Sin datos todavía — instala el Kitxens Pixel arriba y este panel cobra vida con tus visitantes reales. Cero métricas falsas.'
              : 'No data yet — install the Kitxens Pixel above and this panel comes alive with your real visitors. Zero fake metrics.'}
          </p>
        )}
      </RCard>

      {/* Level 3 — Attribution */}
      <RCard className="p-6 mt-4" testid="intel-attribution">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-[15px] font-semibold text-gray-900 flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-[var(--rest-accent)]" /> {isEs ? '¿Qué canal te genera dinero?' : 'Which channel makes you money?'}
          </h2>
          <SourceBadge source={hasPixelData ? 'connected_api' : 'not_connected'} isEs={isEs} />
        </div>
        {(d.attribution.channels || []).length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {d.attribution.channels.map((c) => {
              const on = (d.attribution.connections || {})[c.channel] !== false;
              return (
                <div key={c.channel} className={`rounded-xl border p-4 transition-all ${on ? 'border-gray-200' : 'border-dashed border-gray-300 opacity-55 grayscale'}`} data-testid={`attr-channel-${c.channel}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[13px] font-bold text-gray-900 flex items-center gap-1.5">
                      {on && (
                        <span className="relative inline-flex w-1.5 h-1.5" aria-hidden="true">
                          <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
                          <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        </span>
                      )}
                      {(CHANNEL_LABEL[c.channel] || [c.channel, c.channel])[isEs ? 1 : 0]}
                    </p>
                    <button onClick={() => toggleChannel(c.channel, !on)} data-testid={`attr-channel-toggle-${c.channel}`}
                      title={on ? (isEs ? 'Desconectar canal' : 'Disconnect channel') : (isEs ? 'Conectar canal' : 'Connect channel')}
                      className={`relative w-9 h-5 rounded-full transition-colors ${on ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${on ? 'left-[18px]' : 'left-0.5'}`} />
                    </button>
                  </div>
                  <div className="mt-2 space-y-1 text-[12.5px] text-gray-600">
                    {[['visits', isEs ? 'Visitas' : 'Visits', 'visitors'], ['whatsapp', 'WhatsApp', 'whatsapp'], ['calls', isEs ? 'Llamadas' : 'Calls', 'calls'], ['reservations', isEs ? 'Reservas' : 'Reservations', 'reservations'], ['orders', isEs ? 'Pedidos' : 'Orders', 'orders']].map(([mk, ml, selKey]) => (
                      <p key={mk} className={`flex justify-between rounded px-1 -mx-1 transition-colors ${sel === selKey ? 'bg-[var(--rest-accent-soft)] font-bold text-gray-900' : ''}`}>
                        <span>{ml}</span><b className="tabular-nums">{c[mk]}</b>
                      </p>
                    ))}
                  </div>
                  <p className="mt-2 pt-2 border-t border-gray-100 flex justify-between text-[13px]">
                    <span className="text-gray-500">{on ? (isEs ? 'Revenue est.' : 'Est. revenue') : (isEs ? 'Desconectado' : 'Disconnected')}</span>
                    <b className="text-[var(--rest-accent)] tabular-nums">{on ? money(c.est_revenue) : '—'}</b>
                  </p>
                </div>
              );
            })}
            <AiTeamRotator isEs={isEs} channels={d.attribution.channels} connections={d.attribution.connections}
              onReconnect={(ch) => toggleChannel(ch, true)} />
          </div>
        ) : (
          <p className="text-[13.5px] text-gray-500 py-4 text-center">
            {isEs ? 'La atribución por canal aparecerá con las primeras visitas del pixel.' : 'Channel attribution appears with your first pixel visits.'}
          </p>
        )}
        {(d.attribution.campaigns || []).length > 0 && (
          <p className="mt-3 text-[12px] text-gray-500">
            {isEs ? 'Campañas activas: ' : 'Active campaigns: '}
            {d.attribution.campaigns.map((c) => `${c.campaign} (${c.visits})`).join(' · ')}
          </p>
        )}
      </RCard>

      {/* Real sales rollups */}
      <RCard className="p-6 mt-4" testid="intel-sales">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-[15px] font-semibold text-gray-900">{isEs ? 'Ventas reales — resumen por periodo' : 'Real sales — period summary'}</h2>
          <SourceBadge source={d.sales.has_data ? 'manual_imports' : 'not_connected'} isEs={isEs} />
        </div>
        {d.sales.has_data ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px]">
                <thead>
                  <tr className="text-left text-[10.5px] uppercase tracking-wider text-gray-400">
                    <th className="pb-2 font-bold">{isEs ? 'Periodo' : 'Period'}</th>
                    <th className="pb-2 font-bold text-right">{isEs ? 'Ventas' : 'Sales'}</th>
                    <th className="pb-2 font-bold text-right">{isEs ? 'Órdenes' : 'Orders'}</th>
                    <th className="pb-2 font-bold text-right">{isEs ? 'Ticket prom.' : 'Avg ticket'}</th>
                  </tr>
                </thead>
                <tbody>
                  {[['daily', 'Último día', 'Latest day'], ['weekly', 'Semana (7d)', 'Week (7d)'],
                    ['biweekly', 'Quincena (14d)', 'Biweekly (14d)'], ['monthly', 'Mes (30d)', 'Month (30d)'],
                    ['quarterly', 'Trimestre (90d)', 'Quarter (90d)'], ['yearly', 'Año (365d)', 'Year (365d)']].map(([k, es, en]) => {
                    const p = d.sales.periods[k];
                    return (
                      <tr key={k} className="border-t border-gray-100" data-testid={`sales-period-${k}`}>
                        <td className="py-2 text-[13px] text-gray-700">{isEs ? es : en}</td>
                        <td className="py-2 text-[13px] font-semibold text-right tabular-nums">{money(p.total)}</td>
                        <td className="py-2 text-[13px] text-right tabular-nums">{p.orders}</td>
                        <td className="py-2 text-[13px] text-right tabular-nums">${p.avg_ticket}</td>
                      </tr>
                    );
                  })}
                  <tr className="border-t-2 border-gray-200" data-testid="sales-period-lifetime">
                    <td className="py-2 text-[13px] font-bold text-gray-900">{isEs ? 'Histórico total' : 'Lifetime'}</td>
                    <td className="py-2 text-[13px] font-bold text-right tabular-nums">{money(d.sales.lifetime.total)}</td>
                    <td className="py-2 text-[13px] font-bold text-right tabular-nums">{d.sales.lifetime.orders}</td>
                    <td className="py-2 text-[13px] font-bold text-right tabular-nums">${d.sales.lifetime.avg_ticket}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-[11px] text-gray-400">
              {isEs ? `Datos hasta ${d.sales.data_through} · Sube reportes nuevos en ` : `Data through ${d.sales.data_through} · Upload fresh reports in `}
              <a href="/restaurant/reports" className="text-[var(--rest-accent)] font-semibold hover:underline">Reports</a>
              {isEs ? ' — cuando conectemos tu POS por API, esto se actualizará solo.' : ' — once your POS is connected via API this updates itself.'}
            </p>
          </>
        ) : (
          <p className="text-[13.5px] text-gray-500 py-4 text-center" data-testid="intel-sales-empty">
            {isEs ? <>Aún no hay ventas cargadas. Sube tu primer reporte CSV en <a href="/restaurant/reports" className="text-[var(--rest-accent)] font-semibold hover:underline">Reports</a> y aquí verás el resumen diario, semanal, quincenal, mensual y anual.</>
              : <>No sales data yet. Upload your first CSV report in <a href="/restaurant/reports" className="text-[var(--rest-accent)] font-semibold hover:underline">Reports</a> and this becomes your daily, weekly, biweekly, monthly and yearly summary.</>}
          </p>
        )}
      </RCard>
    </div>
  );
}
